/**
 * Haunted house (Halloween): `input_boolean.haunted_house` (lib/house.ts hauntedId) drives a Home switch + banner,
 * a few app touches (pumpkin in the app bar, orange accent via html[data-haunted]) and the wall tablets' Halloween
 * theme (screens/tablet/Halloween.tsx). HA runs the lights and sounds; the server screams through the doorbell.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { store } from './store.js';
import { haptic } from './native.js';
import { HAUNTED_ID, hauntedId } from './house.js';
import { useAccess, useEntity, useEntityAccess, useModel } from './hooks.js';

/** October only: the switch is offered on Home (owner, 2026-11-01). While on, the banner shows whatever the month. */
export function halloweenSeason(d: Date): boolean {
  return d.getMonth() === 9;
}

export const SPOOKY_GREETINGS = ['Happy Halloween', 'Enter if you dare…', 'Trick or treat?', 'Something wicked this way comes', 'Mind the bats…'] as const;

/** The tablets' greeting while haunted: a different line every 10 minutes. */
export function spookyGreeting(d: Date): string {
  return SPOOKY_GREETINGS[Math.floor(d.getTime() / 600_000) % SPOOKY_GREETINGS.length]!;
}

/** Screen flicker (failing candlelight / power): every 30–120 s, lasting 250–900 ms (`r` in [0, 1)). */
export const FLICKER_GAP_MS = { min: 30_000, max: 120_000 } as const;
export const FLICKER_LEN_MS = { min: 250, max: 900 } as const;
/** The tablets' flicker patterns (halloween.css .hw-flicker[data-variant]): the n-th flicker uses pattern n mod this. */
export const FLICKER_VARIANTS = 3;
export const flickerVariant = (n: number): number => ((Math.trunc(n) % FLICKER_VARIANTS) + FLICKER_VARIANTS) % FLICKER_VARIANTS;
export const nextFlickerIn = (r: number = Math.random()): number => Math.round(FLICKER_GAP_MS.min + r * (FLICKER_GAP_MS.max - FLICKER_GAP_MS.min));
export const flickerMs = (r: number = Math.random()): number => Math.round(FLICKER_LEN_MS.min + r * (FLICKER_LEN_MS.max - FLICKER_LEN_MS.min));

export interface Haunted {
  /** The helper's id when this user can see it. */
  id: string | null;
  on: boolean;
  canControl: boolean;
  reason: string | null;
}

export function useHaunted(home: string): Haunted {
  const model = useModel(home);
  const access = useAccess(home);
  const id = hauntedId(model, access);
  const st = useEntity(home, id ?? '');
  const acc = useEntityAccess(home, id ?? '');
  return {
    id,
    on: !!id && st?.s === 'on',
    canControl: !!id && acc.canControl && !!st && st.s !== 'unavailable',
    reason: acc.reason,
  };
}

/** Through the normal gateway (input_boolean turn_on / turn_off; the house setting needs `control`). */
export function setHaunted(home: string, on: boolean): void {
  haptic('medium');
  void store
    .call(home, 'input_boolean', on ? 'turn_on' : 'turn_off', [HAUNTED_ID], undefined, {
      label: 'Haunted house',
      optimistic: (s) => ({ ...s, s: on ? 'on' : 'off' }),
    })
    .catch(() => undefined);
}

const reducedMotion = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Random screen flicker while `enabled` (none with reduced motion). `n` changes per flicker (key the overlay on it so
 * the animation restarts), `ms` is its length (null: not flickering); `flick()` forces one now (a doorbell ring).
 */
export function useFlicker(enabled: boolean): { n: number; ms: number | null; flick: () => void } {
  const [state, setState] = useState<{ n: number; ms: number | null }>({ n: 0, ms: null });
  const off = useRef<number | undefined>(undefined);
  const flick = useCallback(() => {
    if (!enabled || reducedMotion()) return;
    const ms = flickerMs();
    setState((s) => ({ n: s.n + 1, ms }));
    window.clearTimeout(off.current);
    off.current = window.setTimeout(() => setState((s) => ({ ...s, ms: null })), ms);
  }, [enabled]);
  useEffect(() => {
    if (!enabled) {
      window.clearTimeout(off.current);
      setState((s) => (s.ms === null ? s : { ...s, ms: null }));
      return;
    }
    let t: number | undefined;
    const loop = (): void => { t = window.setTimeout(() => { flick(); loop(); }, nextFlickerIn()); };
    loop();
    return () => window.clearTimeout(t);
  }, [enabled, flick]);
  useEffect(() => () => window.clearTimeout(off.current), []);
  return { ...state, flick };
}
